'use client';

import { useTranslations } from 'next-intl';
import { VStack } from '@terra/ui-styled-system';
import type {
  CvssEnvironmentalRequirements,
  CvssMetricReason,
  FindingCvssVersionKey,
} from '@terra/shared-cvss';
import { HeadingItem } from '../../components/typography';
import { CvssCalculator } from '../CVSS/CvssCalculator';
import { CvssMetricReasonsFields } from '../CVSS/CvssMetricReasonsFields';
import type { EditableFields } from './finding-editable-fields';

export type FindingCvssVersionEditorProps = {
  version: FindingCvssVersionKey;
  cvss: EditableFields['cvss'];
  originalCvss: EditableFields['cvss'];
  environmentalRequirements?: Partial<CvssEnvironmentalRequirements> | null;
  onChange: (cvss: EditableFields['cvss']) => void;
};

export function FindingCvssVersionEditor({
  version,
  cvss,
  originalCvss,
  environmentalRequirements,
  onChange,
}: FindingCvssVersionEditorProps) {
  const t = useTranslations('designSystem.findings.cvssVersions');
  const handleScoreChange = (score: string, vector: string) =>
    onChange(
      score && vector
        ? {
            score,
            vector,
            version,
            environmentalScore: null,
            environmentalVector: null,
            metricReasons: cvss?.metricReasons ?? [],
            explanation: cvss?.explanation ?? null,
          }
        : null,
    );
  const handleReasonsChange = ({
    metricReasons,
    explanation,
  }: {
    metricReasons: CvssMetricReason[];
    explanation: string;
  }) => onChange(cvss ? { ...cvss, metricReasons, explanation } : cvss);

  return (
    <VStack gap={3} alignItems="stretch" w="full">
      <HeadingItem>{t('editorTitle', { version })}</HeadingItem>
      <CvssCalculator
        key={version}
        initialScore={cvss?.score || ''}
        initialVector={cvss?.vector || ''}
        initialEnvironmentalVector={cvss?.environmentalVector}
        version={version}
        findingVersion={version}
        environmentalRequirements={
          version === '4.0' ? environmentalRequirements : undefined
        }
        onScoreChange={handleScoreChange}
      />
      <CvssMetricReasonsFields
        originalCvss={originalCvss}
        cvss={cvss}
        onReasonsChange={handleReasonsChange}
      />
    </VStack>
  );
}
